import { createFileRoute, Outlet } from "@tanstack/react-router";
import { PlugZapIcon } from "@quake/shared-ui/components/animate-ui/icons/plug-zap";
import { AnimateIcon } from "@quake/shared-ui/components/animate-ui/icons/icon";
import z from "zod";
import { OAuthErrorComponent } from "./_oauth/-error-component";
import { cn } from "@quake/shared-ui/lib/utils";
import { Card, CardContent } from "@quake/shared-ui/components/ui/card";
import Logo from "@assets/images/quake_logo.svg";
import ConnectionIcon from "lucide-react/dist/esm/icons/chevrons-left-right-ellipsis";
import { AnimatedBall } from "@quake/shared-ui/automation-ui/edges/AnimatedEdge";
export const Route = createFileRoute("/_oauth")({
  component: RouteComponent,
  errorComponent: OAuthErrorComponent,
  validateSearch: (search) =>
    z
      .object({
        error: z.string().optional(),
        error_description: z.string().optional(),
        code: z.string().optional(),
        partner: z
          .object({
            id: z.string(),
            logo_url: z.url().optional(),
            name: z.string().optional(),
            website_url: z.url().optional(),
          })
          .optional(),
      })
      .parse(search),
});

function RouteComponent() {
  const { error, error_description, partner } = Route.useSearch() ?? {};
  return (
    <DotBackgroundDemo>
      <style>
        {`
        .animated-line {
          animation: slide-infinite 1.5s linear infinite;
        }

        @keyframes slide-infinite {
          0% {
            transform: translateY(-40%);
          }
          100% {
            transform: translateY(0);
          }
        }`}
      </style>
      <div className="h-screen grid pt-8 overflow-y-auto contain-size">
        <div className="flex flex-col gap min-w-80 max-w-screen mx-auto mb-32">
          <Card className="p-2! w-auto! mx-auto">
            <CardContent className="flex justify-center p-0 w-auto!">
              <div className="flex items-center justify-center gap-2 max-h-36">
                <img
                  src={Logo}
                  alt="Quake Logo"
                  className="w-12 rounded overflow-hidden border border-solid p-1 dark:invert"
                />
                {partner ? (
                  <>
                    <ConnectionIcon className="stroke-[#3b82f6]" size="24" />
                    <a
                      href={partner?.website_url ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded overflow-hidden border border-solid p-1"
                    >
                      <img
                        src={partner?.logo_url}
                        alt={`${partner?.name ?? "Partner"} logo`}
                        className="w-12 object-contain dark:backdrop-invert dark:p-2 dark:rounded-xl"
                      />
                    </a>
                  </>
                ) : null}
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-center h-14 overflow-hidden">
            <svg width="2" height="60" viewBox="0 0 2 60" className="overflow-visible translate-x-[-5px]">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#1e40af" stopOpacity="1" />
                  <stop offset="100%" stopColor="#1e40af" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M 1 0 L 1 80" strokeWidth="2" fill="none" strokeDasharray="8 4" className="stroke-[#3b82f6]">
                <animate attributeName="stroke-dashoffset" from="50" to="0" dur="1.5s" repeatCount="indefinite" />
              </path>
              <AnimatedBall
                id="animated-ball"
                edgePath="M 1 0 L 1 80"
                style={{ stroke: "#3b82f6" }}
                pointsPerSecond={55}
              />
            </svg>
          </div>
          <Card className="grid items-center gap-2">
            {error ? (
              <div className="w-80">
                <OAuthErrorComponent error={error} errorDescription={error_description} />
              </div>
            ) : (
              <Outlet />
            )}
          </Card>
        </div>
      </div>
    </DotBackgroundDemo>
  );
}

export function DotBackgroundDemo({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex h-200 w-full items-center justify-center bg-slate-100 dark:bg-black">
      <div
        className={cn(
          "absolute inset-0",
          "bg-size-[20px_20px]",
          "bg-[radial-gradient(#d4d4d4_1px,transparent_1px)]",
          "dark:bg-[radial-gradient(#404040_1px,transparent_1px)]",
        )}
      />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-100 mask-[radial-gradient(ellipse_at_center,transparent_30%,black)] dark:bg-black"></div>
      <div className="relative z-20 w-full">{children}</div>
    </div>
  );
}
